import React, { memo, useCallback, useContext, useState } from 'react';
import Drawer from '../../../BaseComponents/Drawers/Drawer/EDDrawer';
import EDLinkListItem from '../../../BaseComponents/List/LinkList/EDLinkListItem';
import EDIcon from '../../../BaseComponents/Icon/EDIcon';
import { Theme } from '@mui/material/styles';
import { withStyles } from 'tss-react/mui';
import DelsystemView from './Components/DelsystemView';
import Logo from '../../../Assets/Branding/Aveny_logo.svg';
import VersionLink from '../../AppNavbar/Components/VersionLink/VersionLink';
import EDIconButton from '../../../BaseComponents/Buttons/IconButton/EDIconButton';
import {
  IDatabase,
  LoginStateContext,
} from '../../../Features/Login/Contexts/LoginStateContext/LoginStateContext';
import EDSearchBar from '../../../BaseComponents/SearchBar/EDSearchBar';
import DatabaseList from '../../DatabaseList/DatabaseList';
import LogoutHandler from '../../AppNavbar/Components/UserMenu/Components/LogoutHandler';
import EDBox from '../../../BaseComponents/EDBox/EDBox';
import EDButton from '../../../BaseComponents/Buttons/Button/EDButton';
import RouterLink from '../../RouterLink/RouterLink';
import {
  buildBasePath,
  useBaseRouteValue,
} from '../../../Features/Routing/Utils/routerUtils';
import EDMenuItem from '../../../BaseComponents/Menu/EDMenuItem';
import EDTypography from '../../../BaseComponents/Typography/EDTypography';
import EDOverlay from '../../../BaseComponents/Overlay/EDOverlay';
import EDAvatar from '../../../BaseComponents/Avatar/EDAvatar';
import { IMessage } from '../../Messages/Messages';
import EDDivider from '../../../BaseComponents/Divider/EDDivider';
import { APP_NAVBAR_HEIGHT_PX } from '../../../BaseComponents/ThemeProvider/AvenyTheme';
import { useIsTouchDevice } from '../../BreakpointContext/BreakpointContextUtils';

interface IProps {
  readonly databases: IDatabase[];
  activeDatabase: string | null;
  open: boolean;
  onToggle: () => void;
  anchor?: 'left' | 'right';
  searchBarRef?: React.MutableRefObject<HTMLInputElement | null>;
  onUserMenuClick?: (event: React.MouseEvent<HTMLElement>) => void;
  onToggleTaskMenu?: () => void;
  messages?: IMessage[];
  classes?: any;
}

interface IState {
  activeSubsystemCode: string[];
  activeSubsystemExpanded: boolean;
  startedRoutinesExpanded: boolean;
  routineSearch: string;
}

const styles = (theme: Theme) => ({
  databaseListContainer: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    justifyContent: 'flex-start',
  },
  notificationOverlay: {
    top: -8,
    right: -8,
  },
  logo: {
    width: theme.spacing(5),
    height: theme.spacing(5),
    '& img': {
      width: theme.spacing(5),
      height: theme.spacing(5),
    },
  },
  logoButton: {
    marginRight: theme.spacing(0.5),
    marginLeft: 0,
    marginTop: 0,
    marginBottom: 0,
  },
  footerMenuItemIcon: {
    marginLeft: theme.spacing(0.5),
  },
});

const EDDrawerWrapper: React.FC<React.PropsWithChildren<{}>> = ({
  children,
}) => {
  return (
    <EDBox sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {children}
    </EDBox>
  );
};

/**
 * Huvudmeny som visas till vänster i programmet
 */
export const MainMenu: React.FC<React.PropsWithChildren<IProps>> = memo(
  props => {
    const baseRouteValues = useBaseRouteValue();
    const isTouchDevice = useIsTouchDevice();
    const loginStateContext = useContext(LoginStateContext);
    const [state, setState] = useState<IState>({
      activeSubsystemCode: [],
      activeSubsystemExpanded: false,
      startedRoutinesExpanded: true,
      routineSearch: '',
    });

    const setRoutineSearch = useCallback((value: string) => {
      setState(state => ({
        ...state,
        routineSearch: value,
        startedRoutinesExpanded: false,
      }));
    }, []);

    const setActiveSubsystemExpanded = useCallback((value: boolean) => {
      setState(state => ({
        ...state,
        activeSubsystemExpanded: true,
      }));
    }, []);

    const toggleSubsystem = useCallback((code: string) => {
      setState(state => ({
        ...state,
        activeSubsystemCode: state.activeSubsystemCode.includes(code)
          ? ['']
          : [code],
        activeSubsystemExpanded: false,
      }));
    }, []);

    const setSubsystemsOpen = useCallback((codes: string[]) => {
      setState(state => ({
        ...state,
        activeSubsystemCode: codes,
        activeSubsystemExpanded: false,
      }));
    }, []);

    const toggleStartedRoutinesExpanded = useCallback(() => {
      setState(prevState => ({
        ...prevState,
        startedRoutinesExpanded: !prevState.startedRoutinesExpanded,
      }));
    }, []);

    const database = props.databases.find(x => x.name === props.activeDatabase);
    const unreadCount =
      props.messages?.filter(m => !m.reciever.read).length ?? 0;

    const getHeaderContent = (): React.ReactNode => {
      return (
        <React.Fragment>
          <div onClick={props.onToggle}>
            {window.env.showDemoComponents && (
              <EDLinkListItem
                to={`/processes`}
                text="Processer"
                icon={<EDIcon iconType="process" color={'inherit'} />}
                type="dark"
                typographyProps={{ variant: 'body2' }}
              />
            )}
          </div>
          {props.open && (
            <EDSearchBar
              autoFocus
              placeholder="Sök rutin.."
              value={state.routineSearch}
              onChange={value => {
                setRoutineSearch(
                  typeof value === 'string' ? value : value.target.value
                );
              }}
              onFocus={event => {
                event.target.select();
              }}
            />
          )}
        </React.Fragment>
      );
    };

    if (!database) {
      return null;
    }

    const startPanelLinkButton = (
      <RouterLink to={buildBasePath(baseRouteValues)}>
        <div onClick={props.onToggle}>
          <EDIconButton
            testId="menuStartpanelButton"
            tooltip="Startpanel"
            icon={
              <img src={Logo} alt="Aveny Logo" className={props.classes.logo} />
            }
            margin="none"
            padding="none"
            iconSize="small"
            className={props.classes.logoButton}
          />
        </div>
      </RouterLink>
    );

    const closeButton = (
      <EDIconButton
        tooltip="Stäng"
        color="inherit"
        onClick={props.onToggle}
        icon={
          <EDIcon
            iconType="close"
            color="inherit"
            className={props.classes.logo}
          />
        }
        // margin="4px 0px 4px 8px"
        padding="none"
        // className={props.classes.logoButton}
      />
    );

    return (
      <Drawer
        type="temporary"
        open={props.open}
        anchor={props.anchor}
        header={{
          onToggle: props.onToggle,
          disabled: !props.activeDatabase,
          disabledReason: 'Korrekt databas saknas',
          content: getHeaderContent(),
          closeIcon: false,
          rightOfIconArea: (
            <EDBox
              sx={{
                display: 'flex',
                alignItems: 'center',
                flex: 1,
                minWidth: 0,
                // marginTop: theme => theme.spacing(0.75),
                height: APP_NAVBAR_HEIGHT_PX,
              }}
            >
              {!isTouchDevice && closeButton}
              {
                <EDBox
                  sx={
                    isTouchDevice
                      ? { marginLeft: theme => theme.spacing(1) }
                      : {}
                  }
                >
                  {startPanelLinkButton}
                </EDBox>
              }
              {isTouchDevice && (
                <>
                  <div className={props.classes.databaseListContainer}>
                    <DatabaseList
                      databases={props.databases}
                      selectedDatabases={database ? [database] : []}
                      size="small"
                    />
                  </div>
                  {closeButton}
                </>
              )}
              {!isTouchDevice && (
                <EDBox sx={{ marginLeft: theme => theme.spacing(1) }}>
                  <VersionLink />
                </EDBox>
              )}
            </EDBox>
          ),
        }}
        component={EDDrawerWrapper}
        toggleClosed={props.onToggle}
        noHeaderPadding
        firstItemsSkipMarginBottom
      >
        <EDBox sx={{ flex: '1 1 auto', overflowY: 'auto' }}>
          <DelsystemView
            activeSubsystemCode={state.activeSubsystemCode}
            activeSubsystemExpanded={state.activeSubsystemExpanded}
            routineSearch={state.routineSearch.toLowerCase()}
            toggleStartedRoutinesExpanded={toggleStartedRoutinesExpanded}
            onToggle={props.onToggle}
            setOpen={setSubsystemsOpen}
            database={database}
            onToggleDelsystem={toggleSubsystem}
            setActiveSubsystemExpanded={setActiveSubsystemExpanded}
            setRoutineSearch={setRoutineSearch}
            startedRoutinesExpanded={state.startedRoutinesExpanded}
          />
        </EDBox>
        {isTouchDevice && (
          <>
            <EDBox
              sx={{
                flexShrink: 0,
                marginTop: 'auto',
                backgroundColor: theme => theme.palette.common.white,
              }}
            >
              <EDDivider />
              <EDMenuItem
                onClick={e => {
                  props.onUserMenuClick?.(e);
                  props.onToggle();
                }}
                heightSmall
                divider
              >
                <EDIcon
                  color="secondary"
                  iconType="person"
                  className={props.classes.footerMenuItemIcon}
                />
                <EDTypography
                  variant="subtitle1"
                  sx={{
                    marginLeft: theme => theme.spacing(2),
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    display: 'block',
                    minWidth: 0,
                    flex: 1,
                  }}
                >
                  {loginStateContext.name || 'Profil'}
                </EDTypography>
              </EDMenuItem>
              <EDMenuItem
                onClick={() => {
                  props.onToggleTaskMenu?.();
                  props.onToggle();
                }}
                heightSmall
              >
                <EDOverlay
                  overlay={
                    unreadCount > 0 && (
                      <EDAvatar
                        sx={{
                          width: 20,
                          height: 20,
                          fontSize: 12,
                          bgcolor: theme =>
                            theme.customProperties.Warning.light,
                        }}
                      >
                        {unreadCount}
                      </EDAvatar>
                    )
                  }
                  classes={{
                    overlay: props.classes.notificationOverlay,
                  }}
                >
                  <EDIcon
                    color="secondary"
                    iconType="assignment"
                    className={props.classes.footerMenuItemIcon}
                  />
                </EDOverlay>
                <EDTypography
                  variant="subtitle1"
                  sx={{ marginLeft: theme => theme.spacing(2) }}
                >
                  Meddelanden
                </EDTypography>
              </EDMenuItem>
            </EDBox>
            <EDBox
              sx={{
                flexShrink: 0,
                backgroundColor: theme => theme.palette.secondary.main,
                color: theme => theme.palette.common.white,
                padding: theme => theme.spacing(0, 1),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <VersionLink />
              <LogoutHandler>
                {handleLogout => (
                  <EDButton
                    onClick={handleLogout}
                    flat
                    icon={
                      <EDIcon iconType="logout" color="inherit" size="small" />
                    }
                    color="inherit"
                    labelNotUpperCase
                  >
                    Logga ut
                  </EDButton>
                )}
              </LogoutHandler>
            </EDBox>
          </>
        )}
      </Drawer>
    );
  }
);
MainMenu.displayName = 'MainMenu';

export default withStyles(MainMenu, styles);
